import { action, computed, makeObservable, observable, runInAction } from "mobx";
import { ApplicationSdk } from "../..";

interface Object_Simple {
    id: number;
    name: string;
}

const formatStringToDate = (dateString: string | null) => {
    if (!dateString) return null;
    const [year, month, day] = dateString.split('-').map(Number);
    return new Date(year, month - 1, day); // Monate sind 0-basiert
}

const formatDate = (date: Date | null) => {
    if (!date) return null;
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0'); // Monate sind 0-basiert
    const year = date.getFullYear();
    return `${year}-${month}-${day}`;
};

/**
 * The details of the categories
 */
class CleanerDialogStore {
    private sdk: ApplicationSdk;

    public activeTab: number = 0;
    public cleaner_id: number | null = null;

    public cleaner_firstname: string = '';
    public cleaner_lastname: string = '';
    public cleaner_userns: string = '';
    public cleaner_phonenumber: string = '';
    public cleaner_onboarding_date: any = null;
    public cleaner_offboarding_date: any = null;
    public cleaner_assigned_objects: Set<number> = new Set();
    public cleaner_onboarding_now: boolean = false;

    public offboardingEnabled: boolean = false;

    public cleanerDataLoading: boolean = false;
    public objectDataLoading: boolean = false;

    public assignedObjects: Set<number> = new Set();
    public unassignedObjects: Set<number> = new Set();
    public availableObjects: Map<number, string> = new Map();

    public trainings: Array<any> = [];

    constructor(sdk: ApplicationSdk) {
        makeObservable(this, {
            activeTab: observable,
            cleaner_id: observable,
            cleaner_onboarding_now: observable,
            cleaner_firstname: observable,
            cleaner_lastname: observable,
            cleaner_userns: observable,
            cleaner_phonenumber: observable,
            cleaner_assigned_objects: observable,
            cleanerDataLoading: observable,
            objectDataLoading: observable,
            assignedObjects: observable,
            cleaner_offboarding_date: observable,
            cleaner_onboarding_date: observable,
            unassignedObjects: observable,
            availableObjects: observable,
            offboardingEnabled: observable,
            trainings: observable,
            fetchCleaner: action,
            fetchObjects: action,
            switchOnboardingNow: action,
            switchOffboarding: action,
            updateObjectAssignment: action,
            onSaveCleaner: action,
            onChangeTab: action,
            updateCleanerAssignment: action,
            assignedObjectsArray: computed,
            unassignedObjectsArray: computed,
            onboardingDate: computed,
            offboardingDate: computed,
            isOffboardingEnabled: computed,
            isLoading: computed,
            isValid: computed,
            lastname: computed,
            firstname: computed,
            validFirstname: computed,
            validPhonenumber: computed,
            validLastname: computed,
            isNewCleaner: computed,
            validOnboarding: computed,
            id: computed
        });
        this.sdk = sdk;

    }

    public async init(config: any): Promise<void> {
        if (config?.cleaner_id) {
            this.cleaner_id = config.cleaner_id;
        }

        await this.fetchCleaner(this.cleaner_id || null);
    }


    public onChangeTab(tab: number): void {
        if (tab === 1) {
            this.fetchTrainings();
        } else if (tab === 0) {
            // this.fetchObjects();
        }
        this.activeTab = tab;
    }

    public async fetchCleaner(id: number | null): Promise<void> {
        runInAction(() => {
            this.cleanerDataLoading = true;
        });

        const cleanerDetails: any = await this.sdk.fetchApi(
            'cleaner',
            'get',
            { cleaner_id: id }
        );

        runInAction(() => {
            this.cleanerDataLoading = false;

            if (cleanerDetails?.status === 200) {
                const { cleaner, assigned_objects, object_cache } = cleanerDetails.json || {};
                if (cleaner) {
                    this.cleaner_firstname = cleaner.firstname;
                    this.cleaner_lastname = cleaner.lastname;
                    this.cleaner_phonenumber = cleaner.phonenumber;
                    this.cleaner_userns = cleaner.user_ns;
                    this.cleaner_onboarding_date = formatStringToDate(cleaner.onboarding_date);
                    this.cleaner_offboarding_date = formatStringToDate(cleaner.offboarding_date);
                    this.cleaner_onboarding_now = !!cleaner.onboarding_directly;
                    this.offboardingEnabled = cleaner.offboarding_date !== null;
                }

                if (assigned_objects) {
                    this.cleaner_assigned_objects = new Set(assigned_objects.map((cleaner: any) => cleaner.id));

                    assigned_objects.forEach((obj: any) => {
                        if (!this.availableObjects.has(obj.id)) {
                            this.availableObjects.set(obj.id, obj.name);
                        }

                        if (this.cleaner_assigned_objects.has(obj.id)) {
                            this.assignedObjects.add(obj.id);
                        } else {
                            this.unassignedObjects.add(obj.id);
                        }
                    });
                }
                if (object_cache) {
                    if (this.sdk.objects.needRefresh(object_cache)) {
                        this.fetchObjects();
                    } else {
                        this.updateObjectAssignment(this.sdk.objects.all.data);
                    }
                }

            }
        });
    }

    public async fetchObjects(): Promise<void> {
        const objectList: any = await this.sdk.fetchApi(
            'object',
            'query',
            {
                format: 'simple',
                limit: 999
            }
        );

        runInAction(() => {
            this.objectDataLoading = false;

            if (objectList?.status === 200) {
                const allObjects: any = this.sdk.objects.update(
                    objectList.json.data,
                    objectList.json.object_cache
                );
                this.updateObjectAssignment(allObjects || []);
            }
        });

    }

    public async fetchTrainings(): Promise<void> {

        // If there is no cleaner_id, we don't need to fetch trainings
        if (!this.cleaner_id) return;

        // Reset
        runInAction(() => {
            // this.productDataLoading = true;
        })

        const trainingList: any = await this.sdk.fetchApi(
            'cleaner',
            'training',
            {
                cleaner_id: this.cleaner_id
            }
        );

        runInAction(() => {
            // this.productDataLoading = false;

            if (trainingList?.status === 200) {
                runInAction(() => {
                    this.trainings = (trainingList.json.trainings || []).map((training: any) => ({
                        ...training,
                        image: training.image ?
                            trainingList.json.s3urltraining + training.image + '.jpeg' :
                            null,
                    }));
                });
                // this.productDataFetched = true;

                // (trainingList.json.data as Array<any>).forEach((p: any) => {
                //     if (!this.availableProducts.has(p.id)) {
                //         this.availableProducts.set(p.id, p);
                //     }

                //     // add this object to unassigned if it is not already assigned
                //     if (this.object_assigned_products.has(p.id)) {
                //         this.assignedProducts.add(p.id);
                //     } else {
                //         this.unassignedProducts.add(p.id);
                //     }
                // });
            }
        });

    }

    public updateObjectAssignment(allObjects: Array<any> = []): void {
        this.availableObjects.clear();
        allObjects.forEach((object: any) => {
            if (!this.availableObjects.has(object.id)) {
                this.availableObjects.set(object.id, object.name);
            }

            // add this object to unassigned if it is not already assigned
            if (this.cleaner_assigned_objects.has(object.id)) {
                this.assignedObjects.add(object.id);
            } else {
                this.unassignedObjects.add(object.id);
            }
        });
    }
    public async onSaveCleaner(): Promise<void> {
        const useOnboardingDate = (this.cleaner_onboarding_now ? new Date() : this.cleaner_onboarding_date) || null;
        const useOffboardingDate = (this.offboardingEnabled ? this.cleaner_offboarding_date : null) || null;

        if (this.isNewCleaner && useOffboardingDate && useOffboardingDate <= useOnboardingDate) {
            throw new Error('Das Offboarding Datum muss nach dem Onboarding Datum liegen!');
        }
        const data = {
            firstname: this.firstname,
            lastname: this.lastname,
            phonenumber: this.phonenumber,
            onboarding_directly: this.cleaner_onboarding_now ? 1 : 0,
            onboarding_date: this.cleaner_onboarding_now ?
                formatDate(new Date()) :
                formatDate(this.cleaner_onboarding_date) || null,
            offboarding_date: this.offboardingEnabled ? formatDate(this.cleaner_offboarding_date) : null,
            assigned_objects: Array.from(this.assignedObjects).map((id: number) => id),
            cleaner_id: this.cleaner_id || undefined,
        }

        const cleanerRequest: any = await this.sdk.fetchApi(
            'cleaner',
            this.cleaner_id ? 'update' : 'add',
            data
        );

        if (cleanerRequest?.status !== 201 && cleanerRequest?.status !== 200) {
            const errorCode = cleanerRequest?.json?.error || '';

            if (errorCode) {
                switch (errorCode) {
                    case 'ERR_VALIDATE_FIRSTNAME':
                        throw new Error('Bitte einen gültigen Vornamen angeben!');
                    case 'ERR_VALIDATE_LASTNAME':
                        throw new Error('Bitte einen gültigen Nachnamen angeben!');
                    case 'ERR_VALIDATE_PHONENUMBER':
                        throw new Error('Bitte eine gültige Rufnummer angeben. 49(Vorwahl)(Rufnummer)!');
                    case 'ERR_VALIDATE_ONBOARDING_DATE':
                        throw new Error('Bitte gebe ein Onboarding Datum an!');
                    case 'ERR_DUPLICATED_PHONENUMBER':
                        throw new Error('Diese Rufnummer ist bereits vergeben!');
                    //     case 'ERR_PRODUCT_NOT_ADDED':
                    //         throw new Error('Das Produkt konnte nicht hinzugefügt werden');
                    //     case 'ERR_VALIDATE_CATEGORY_ID':
                    //         throw new Error('Bitte wählen Sie eine Kategorie aus');
                    //     case 'ERR_VALIDATE_NAME':
                    //         throw new Error('Bitte geben Sie einen Produktnamen ein');
                    //     case 'ERR_VALIDATE_ARTICLE_NUMBER':
                    //         throw new Error('Bitte geben Sie eine gültige Artikelnummer ein (1-9999)');
                    default:
                        break;
                }
            }

            throw new Error('Ein unbekannter Fehler ist aufgetreten');
        }
    }


    public updateCleanerAssignment(unassigned: Array<Object_Simple>, assigned: Array<Object_Simple>): void {
        this.assignedObjects = new Set(assigned.map(obj => obj.id));
        this.unassignedObjects = new Set(unassigned.map(obj => obj.id));
    }

    public switchOnboardingNow(): void {
        this.cleaner_onboarding_now = !this.cleaner_onboarding_now;
    }

    public switchOffboarding(): void {
        this.offboardingEnabled = !this.offboardingEnabled;
    }

    get assignedObjectsArray(): Array<Object_Simple> {
        return Array.from(this.assignedObjects).map((id: number) => {
            return {
                id: id,
                name: this.availableObjects.get(id) || '?'
            }
        });
    }

    get unassignedObjectsArray(): Array<Object_Simple> {
        return Array.from(this.unassignedObjects).map((id: number) => {
            return {
                id: id,
                name: this.availableObjects.get(id) || '?'
            }
        });
    }

    set firstname(name: string) {
        // limit to 40 characters
        if (name.length <= 40) {
            runInAction(() => {
                this.cleaner_firstname = name;
            });
        }
    }

    get firstname(): string {
        return this.cleaner_firstname;
    }

    set lastname(name: string) {
        // limit to 40 characters
        if (name.length <= 40) {
            runInAction(() => {
                this.cleaner_lastname = name;
            });
        }
    }

    get lastname(): string {
        return this.cleaner_lastname;
    }

    set phonenumber(phonenumber: string) {
        // limit to 40 characters
        if (phonenumber.length <= 40) {
            runInAction(() => {
                this.cleaner_phonenumber = phonenumber;
            });
        }
    }

    get phonenumber(): string {
        return this.cleaner_phonenumber;
    }

    set onboardingDate(date: any) {
        this.cleaner_onboarding_date = date || null;
    }

    get onboardingDate(): any {
        if (!this.cleaner_onboarding_date) return null;

        // console.log('Onboarding Date', this.cleaner_onboarding_date)
        // const [day, month, year] = this.cleaner_onboarding_date.split('-').map(Number);
        // return new Date(year, month - 1, day); // Month is 0-based in JavaScript
        return this.cleaner_onboarding_date;
    }

    set offboardingDate(date: any) {
        this.cleaner_offboarding_date = date || null;
    }

    get offboardingDate(): Date | null {
        if (!this.cleaner_offboarding_date) return null;

        return this.cleaner_offboarding_date;
    }

    get isOnboardingNow() {
        return this.cleaner_onboarding_now;
    }

    get isOffboardingEnabled() {
        return this.offboardingEnabled;
    }

    get isNewCleaner(): boolean {
        return this.cleaner_id === null;
    }

    get saveButton(): string {
        return this.cleaner_id ? 'Aktualisieren' : 'Erstellen';
    }

    get header(): string {
        return this.cleaner_id ? 'Reinigungskraft bearbeiten' : 'Neue Reinigungskraft anlegen';
    }

    get id(): string {
        return 'cleaner';
    }

    get isLoading(): boolean {
        return this.cleanerDataLoading;
    }

    get validFirstname(): boolean {
        return this.cleaner_firstname.length >= 3;
    }

    get validLastname(): boolean {
        return this.cleaner_lastname.length >= 3;
    }

    get validPhonenumber(): boolean {
        const phonePattern = /^49(?!0)\d{9,13}$/;
        return phonePattern.test(this.cleaner_phonenumber);
    }

    get validOnboarding(): boolean {
        return this.cleaner_onboarding_date !== null || this.cleaner_onboarding_now;
    }

    get isValid(): boolean {
        return this.validFirstname && this.validLastname && this.validPhonenumber && this.validOnboarding;
    }
}

export default CleanerDialogStore;